"use client";
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import Link from "next/link";

interface SlideItem {
  image: string;
  link: string | null;
}

interface BannerClientProps {
  companyId: string;
  keywords: string[];
  merchants: any[];
  slides: SlideItem[];
}

export default function BannerClient({
  companyId,
  keywords,
  merchants,
  slides,
}: BannerClientProps) {
  const [bubbles, setBubbles] = useState<any[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  const displayTags = keywords?.slice(0, 6) ?? [];

  // Auto Slider Timer
  useEffect(() => {
    if (!slides || slides.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }, 4000);

    return () => clearInterval(timer);
  }, [slides]);

  // Merchant Bubbles Logic
  useEffect(() => {
    if (merchants && merchants.length > 0) {
      const selected = merchants.slice(0, 8).map((m, i) => ({
        name: m.merchant_name,
        logo: m.merchant_logo,
        left: `${i * 12 + 5}%`,
        delay: i * 2,
        duration: 10 + Math.random() * 5,
      }));
      setBubbles(selected);
    }
  }, [merchants]);

  const activeSlide = slides[currentSlide];

  useEffect(() => {
    if (activeSlide) {
      console.log("Current Active Slide Image Path:", activeSlide.image);
    }
  }, [activeSlide]);

  const getImageUrl = (srcString: string) => {
    if (!srcString) return "/placeholder.jpg";
    if (srcString.startsWith("http://") || srcString.startsWith("https://") || srcString.startsWith("/")) {
      return srcString;
    }
    return `https://your-backend-api-domain.com/${srcString}`;
  };

  return (
    <div className="relative z-10 max-w-7xl mx-auto w-full px-6 flex flex-col gap-5 pt-4 md:pt-6 pb-4">
      {/* Layout Grid */}
      <div className="items-center w-full">

        {/* Right Side: Dynamic Cross-fading Slider */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative w-full aspect-[9/3] lg:aspect-[9/3] z-10 order-1 lg:order-2"
        >
          <div className="absolute inset-0 bg-blue-600/10 rounded-[2.5rem] -rotate-2 translate-x-3 translate-y-2"></div>

          <div className="absolute inset-0 rounded-[2.5rem] overflow-hidden border border-indigo-700 shadow-2xl bg-white">
            <AnimatePresence mode="wait">
              {activeSlide?.image && (
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.2, ease: "easeInOut" }}
                  className="absolute inset-0 h-full w-full relative"
                >
                  {activeSlide?.link ? (
                    <Link
                      href={activeSlide.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full h-full relative cursor-pointer"
                    >
                      <Image
                        src={getImageUrl(activeSlide.image)}
                        fill
                        className="object-cover"
                        alt={`Dynamic Slider Item ${currentSlide}`}
                        priority
                        unoptimized
                      />
                    </Link>
                  ) : (
                    <Image
                      src={getImageUrl(activeSlide.image)}
                      fill
                      className="object-cover"
                      alt={`Slider Item ${currentSlide}`}
                      priority
                      unoptimized
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Pagination Dots */}
            {slides.length > 1 && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2">
                {slides.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-1.5 transition-all duration-300 rounded-full ${
                      idx === currentSlide
                        ? "w-6 bg-blue-600"
                        : "w-1.5 bg-white/50"
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Live Badge */}
            <div className="absolute top-6 left-6 z-20 inline-flex items-center gap-2 bg-black/30 backdrop-blur-md border border-white/20 rounded-full px-4 py-1.5">
              <span className="flex h-2 w-2 rounded-full bg-blue-500 animate-pulse"></span>
              <span className="text-[9px] font-black text-white uppercase tracking-[0.2em]">
                Live Premium Discounts
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Trending Keywords Section */}
      {displayTags.length > 0 && (
        <div className="w-full mt-3 md:mt-6 text-center flex flex-col items-center justify-center px-4 sm:px-0">
          {/* Header Box & Text (Perfectly Centered) */}
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center text-blue-400 border border-blue-600/20 shadow-sm">
              <svg
                className="w-4 h-4 transform -rotate-45"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                <line x1="7" y1="7" x2="7.01" y2="7"></line>
              </svg>
            </div>
            <h2 className="text-xl md:text-2xl font-black text-white tracking-tight uppercase m-0 flex items-center gap-2">
              TRENDING{" "}
              <span className="font-serif italic lowercase text-blue-400 font-normal tracking-normal normal-case">
                merchants
              </span>
            </h2>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-2.5 max-w-4xl w-full">
            {displayTags.map((tag, i) => (
              <Link
                key={i}
                href={`/search?query=${encodeURIComponent(tag)}`}
                className="px-4 py-3 bg-indigo-800/60 backdrop-blur-sm border border-indigo-600 text-indigo-200 rounded-full shadow-sm hover:shadow-md hover:border-blue-500 hover:bg-blue-600 hover:text-white transition-all duration-300 hover:-translate-y-0.5 transform no-underline group/tag flex items-center gap-1.5"
              >
                <span className="text-[10px] font-bold uppercase tracking-wider transition-colors">
                  {tag}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
